import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@lancar-pos/ui';
import { formatCurrency } from '@lancar-pos/shared';
import type { ProductVariant } from '@lancar-pos/db-schema';

interface VariantManagerProps {
  variants: ProductVariant[];
  onChange: (variants: ProductVariant[]) => void;
  basePrice: number;
  productId?: string;
  generateSKU: (index: number) => string;
}

export function VariantManager({ variants, onChange, basePrice, generateSKU }: VariantManagerProps) {
  const addVariant = () => {
    const newVariant: ProductVariant = {
      id: crypto.randomUUID(),
      name: '',
      sku: generateSKU(variants.length),
      priceModifier: 0,
      stock: 0,
      isActive: true,
    };
    onChange([...variants, newVariant]);
  };

  const removeVariant = (index: number) => {
    const newVariants = variants.filter((_, i) => i !== index);
    // Re-generate SKUs
    const updatedVariants = newVariants.map((v, i) => ({
      ...v,
      sku: generateSKU(i),
    }));
    onChange(updatedVariants);
  };

  const updateVariant = (index: number, updates: Partial<ProductVariant>) => {
    const newVariants = variants.map((v, i) =>
      i === index ? { ...v, ...updates } : v
    );
    onChange(newVariants);
  };

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs font-medium text-gray-500">
              <th className="pb-2">Nama Varian *</th>
              <th className="pb-2">SKU</th>
              <th className="pb-2">Harga</th>
              <th className="pb-2">Stok</th>
              <th className="pb-2 text-center">Aktif</th>
              <th className="pb-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {variants.map((variant, index) => (
              <tr key={variant.id}>
                <td className="py-2 pr-2">
                  <input
                    type="text"
                    value={variant.name}
                    onChange={(e) => updateVariant(index, { name: e.target.value })}
                    placeholder="Small"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                  />
                </td>
                <td className="py-2 pr-2">
                  <input
                    type="text"
                    value={variant.sku}
                    onChange={(e) => updateVariant(index, { sku: e.target.value })}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded font-mono focus:ring-1 focus:ring-primary-500"
                  />
                </td>
                <td className="py-2 pr-2">
                  <div className="flex items-center gap-2">
                    <select
                      value={variant.priceModifier >= 0 ? '+' : '-'}
                      onChange={(e) => {
                        const isPositive = e.target.value === '+';
                        const absValue = Math.abs(variant.priceModifier);
                        updateVariant(index, { priceModifier: isPositive ? absValue : -absValue });
                      }}
                      className="px-1 py-1.5 text-sm border border-gray-300 rounded"
                    >
                      <option value="+">+</option>
                      <option value="-">-</option>
                    </select>
                    <input
                      type="number"
                      value={Math.abs(variant.priceModifier)}
                      onChange={(e) => {
                        const absValue = parseFloat(e.target.value) || 0;
                        const isPositive = variant.priceModifier >= 0;
                        updateVariant(index, { priceModifier: isPositive ? absValue : -absValue });
                      }}
                      className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                    />
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      = {formatCurrency(basePrice + variant.priceModifier)}
                    </span>
                  </div>
                </td>
                <td className="py-2 pr-2">
                  <input
                    type="number"
                    value={variant.stock}
                    onChange={(e) => updateVariant(index, { stock: parseInt(e.target.value, 10) || 0 })}
                    min="0"
                    className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                  />
                </td>
                <td className="py-2 pr-2 text-center">
                  <input
                    type="checkbox"
                    checked={variant.isActive}
                    onChange={(e) => updateVariant(index, { isActive: e.target.checked })}
                    className="w-4 h-4 text-primary-600 rounded border-gray-300"
                  />
                </td>
                <td className="py-2">
                  <button
                    onClick={() => removeVariant(index)}
                    disabled={variants.length === 1}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Hapus varian"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Button variant="outline" size="sm" onClick={addVariant}>
        <Plus className="w-4 h-4 mr-2" />
        Tambah Varian
      </Button>

      <p className="text-xs text-gray-500">
        Total stok: {variants.reduce((sum, v) => sum + (v.stock || 0), 0)} pcs
      </p>
    </div>
  );
}
